import { assertPaise, type Paise } from './money';

/**
 * Paying GSTR-3B's tax: credit first, in the order the law sets, then cash (sections 49(4), 49(5) and
 * 49A of the CGST Act, rule 88A of the CGST Rules).
 *
 * - IGST credit goes to IGST first; what's left of it to CGST and SGST, in any order and proportion,
 *   and it is used in full before any CGST or SGST credit is (section 49A).
 * - CGST credit goes to CGST, then IGST; SGST credit to SGST, then IGST. CGST credit never pays SGST,
 *   nor SGST credit CGST.
 * - Cess credit pays cess only.
 * - Reverse-charge tax is paid in cash, never from credit (section 49(4)).
 *
 * Where the law leaves a choice (how IGST credit is split between CGST and SGST), it goes where the
 * head's own credit falls short, so the least is paid in cash. What credit doesn't cover is paid from
 * the electronic cash ledger, each head from its own balance, and the rest by challan.
 */

export const TAX_HEADS = ['igst', 'cgst', 'sgst', 'cess'] as const;
export type TaxHead = (typeof TAX_HEADS)[number];
export type ByHead = Readonly<Record<TaxHead, Paise>>;

export const NO_TAX: ByHead = Object.freeze({ igst: 0, cgst: 0, sgst: 0, cess: 0 });

export function byHead(partial: Partial<Record<TaxHead, Paise>> = {}): ByHead {
  return { ...NO_TAX, ...partial };
}

export function addHeads(...items: ByHead[]): ByHead {
  const out = { ...NO_TAX };
  for (const item of items) for (const head of TAX_HEADS) out[head] += item[head];
  return out;
}

export function sumHeads(item: ByHead): Paise {
  return item.igst + item.cgst + item.sgst + item.cess;
}

function assertHeads(item: ByHead | undefined, label: string) {
  if (!item) return;
  for (const head of TAX_HEADS) {
    assertPaise(item[head], `${label}.${head}`);
    if (item[head] < 0) throw new RangeError(`${label}.${head} must not be negative`);
  }
}

export interface SetOffInput {
  /** Tax on sales under forward charge, by head (GSTR-3B Table 3.1 (a) and (b)). */
  liability: ByHead;
  /** Tax under reverse charge (Table 3.1(d)): paid in cash only. */
  reverseCharge?: ByHead;
  /** Credit that can be used this month, by head (Table 4C: net ITC available). */
  credit: ByHead;
  /** Tax already in the electronic cash ledger, by head. */
  cash?: ByHead;
}

/** One use of credit: so much of one head's credit paid so much of a head's tax. */
export interface CreditUse {
  from: TaxHead;
  to: TaxHead;
  amount: Paise;
}

export interface SetOff {
  /** Credit used, in the order the law sets. */
  used: CreditUse[];
  /** Credit used, by the head of the credit. */
  creditUsed: ByHead;
  /** Credit carried forward. */
  creditLeft: ByHead;
  /** Tax paid in cash, by head: what credit didn't cover, plus reverse charge. */
  cashTax: ByHead;
  /** Of that, what the cash ledger already holds, and what's to pay now by challan. */
  fromLedger: ByHead;
  toPay: ByHead;
}

export function setOff(input: SetOffInput): SetOff {
  assertHeads(input.liability, 'liability');
  assertHeads(input.reverseCharge, 'reverseCharge');
  assertHeads(input.credit, 'credit');
  assertHeads(input.cash, 'cash');
  const owed: Record<TaxHead, Paise> = { ...input.liability };
  const credit: Record<TaxHead, Paise> = { ...input.credit };
  const used: CreditUse[] = [];
  const use = (from: TaxHead, to: TaxHead, upTo: Paise) => {
    const amount = Math.min(credit[from], owed[to], Math.max(0, upTo));
    if (amount <= 0) return;
    credit[from] -= amount;
    owed[to] -= amount;
    const last = used[used.length - 1];
    if (last && last.from === from && last.to === to) last.amount += amount;
    else used.push({ from, to, amount });
  };

  // IGST credit: IGST first; then CGST and SGST where their own credit falls short (shared in
  // proportion when it can't cover both), then the rest of it, which must be used before their own.
  use('igst', 'igst', owed.igst);
  const shortC = Math.max(0, owed.cgst - credit.cgst);
  const shortS = Math.max(0, owed.sgst - credit.sgst);
  const forShort = Math.min(credit.igst, shortC + shortS);
  const toC = shortC + shortS > 0 ? Math.floor((forShort * shortC) / (shortC + shortS)) : 0;
  use('igst', 'cgst', toC);
  use('igst', 'sgst', forShort - toC);
  use('igst', 'cgst', owed.cgst);
  use('igst', 'sgst', owed.sgst);
  // CGST and SGST credit: their own head, then IGST.
  use('cgst', 'cgst', owed.cgst);
  use('sgst', 'sgst', owed.sgst);
  use('cgst', 'igst', owed.igst);
  use('sgst', 'igst', owed.igst);
  // Cess for cess.
  use('cess', 'cess', owed.cess);

  const cashTax = addHeads(owed, input.reverseCharge ?? NO_TAX);
  const ledger = input.cash ?? NO_TAX;
  const fromLedger = byHead({
    igst: Math.min(cashTax.igst, ledger.igst),
    cgst: Math.min(cashTax.cgst, ledger.cgst),
    sgst: Math.min(cashTax.sgst, ledger.sgst),
    cess: Math.min(cashTax.cess, ledger.cess),
  });
  const creditUsed = { ...NO_TAX };
  for (const u of used) creditUsed[u.from] += u.amount;
  return {
    used,
    creditUsed,
    creditLeft: { ...credit },
    cashTax,
    fromLedger,
    toPay: byHead({
      igst: cashTax.igst - fromLedger.igst,
      cgst: cashTax.cgst - fromLedger.cgst,
      sgst: cashTax.sgst - fromLedger.sgst,
      cess: cashTax.cess - fromLedger.cess,
    }),
  };
}

const RUPEE = 100;

/** To the nearest rupee, halves up (section 170 of the CGST Act). */
export function roundToRupee(amount: Paise): Paise {
  assertPaise(amount);
  return Math.sign(amount) * Math.round(Math.abs(amount) / RUPEE) * RUPEE;
}

/**
 * Interest on tax paid late in cash (section 50(1)): 18% a year, simple, for each day late, worked
 * out for each tax head and rounded to the rupee, as the portal does. `excluded` is cash that sat in
 * the cash ledger from the due date, which bears none (rule 88B(1), from July 2024).
 */
export function interestByHead(input: {
  cashTax: ByHead;
  days: number;
  excluded?: ByHead;
  percentAYear?: number;
}): ByHead {
  assertHeads(input.cashTax, 'cashTax');
  assertHeads(input.excluded, 'excluded');
  if (!Number.isInteger(input.days)) throw new TypeError('days must be a whole number');
  const basisPoints = BigInt(Math.round((input.percentAYear ?? 18) * 100));
  const out = { ...NO_TAX };
  if (input.days <= 0) return out;
  // In whole numbers throughout (tax × rate × days can pass 2^53), rounding halves up to the rupee.
  const perRupee = 10_000n * 365n * BigInt(RUPEE);
  for (const head of TAX_HEADS) {
    const base = Math.max(0, input.cashTax[head] - (input.excluded?.[head] ?? 0));
    const exact = BigInt(base) * basisPoints * BigInt(input.days);
    out[head] = Number((2n * exact + perRupee) / (2n * perRupee)) * RUPEE;
  }
  return out;
}
